// ABOUTME: Cross-tab claim of an export download, so the file of a finished job is downloaded by
// ABOUTME: exactly one tab of the browser that launched it, even when the launching tab is gone.

const CLAIM_KEY_PREFIX = 'job-download-claim:';
const CLAIM_TTL_MS = 60 * 60 * 1000;
// localStorage is shared by the tabs of one browser only: the launch mark is what tells a tab of
// the launching browser apart from one of the same user on another device or browser.
const LAUNCH_KEY_PREFIX = 'job-launched:';
// A launch mark has to outlive the job it belongs to, which can run for a long time.
const LAUNCH_TTL_MS = 24 * 60 * 60 * 1000;

// A tab that did not launch the job waits this long before claiming the download: the
// launching tab, when still open, claims at once and wins.
const BYSTANDER_CLAIM_DELAY_MS = 600;
// Bystander tabs receive the completion at the same time; spreading their attempts avoids
// two of them reading "unclaimed" in the same instant.
const BYSTANDER_CLAIM_JITTER_MS = 400;

const claimKey = (jobId: string) => `${CLAIM_KEY_PREFIX}${jobId}`;
const launchKey = (jobId: string) => `${LAUNCH_KEY_PREFIX}${jobId}`;

// Claims and launch marks are per job id, so they can only pile up: drop the ones nobody can
// need any more.
const pruneExpired = (prefix: string, ttlMs: number) => {
    const now = Date.now();
    Object.keys(localStorage)
        .filter(key => key.startsWith(prefix))
        .forEach(key => {
            const storedAt = Number(localStorage.getItem(key));
            if (!storedAt || now - storedAt > ttlMs) localStorage.removeItem(key);
        });
};

/**
 * Records that the job was launched from this browser, so any of its tabs announces it and can
 * take the download over when the launching tab is gone. Without a usable localStorage nothing is
 * recorded and only the launching tab announces and downloads.
 */
export const markJobLaunchedInBrowser = (jobId: string) => {
    try {
        pruneExpired(LAUNCH_KEY_PREFIX, LAUNCH_TTL_MS);
        localStorage.setItem(launchKey(jobId), String(Date.now()));
    } catch {
        // Storage unavailable: the job stays known to the launching tab only.
    }
};

export const wasJobLaunchedInBrowser = (jobId: string): boolean => {
    try {
        return Boolean(localStorage.getItem(launchKey(jobId)));
    } catch {
        return false;
    }
};

/**
 * Marks the download of a job as taken by this tab. Returns false when another tab already
 * took it. Without a usable localStorage (private window, blocked storage) the claim always
 * succeeds, since nothing can be shared with the other tabs anyway.
 */
export const claimJobDownload = (jobId: string): boolean => {
    try {
        pruneExpired(CLAIM_KEY_PREFIX, CLAIM_TTL_MS);
        if (localStorage.getItem(claimKey(jobId))) return false;
        localStorage.setItem(claimKey(jobId), String(Date.now()));
        return true;
    } catch {
        return true;
    }
};

export const bystanderClaimDelayMs = () =>
    BYSTANDER_CLAIM_DELAY_MS + Math.random() * BYSTANDER_CLAIM_JITTER_MS;
